import http from './http';

/**
 * Enhanced getUsers supporting all filters from the /users endpoint.
 * Backward compatible signature:
 *  - getUsers(queryString, pageNbr)
 *  - getUsers(paramsObject)
 *
 * paramsObject supports:
 *  - website_id, query, age_min, age_max, page_size, page_nbr, sort_field, sort_type,
 *    size, count, facebook_id, google_id, twitter_id, profile_email, uid
 *
 * When using the queryString form, simple patterns are recognized:
 *  - "facebook:ID" or "facebook_id:ID" → facebook_id=ID (global search)
 *  - "google:ID" or "google_id:ID" → google_id=ID (global search)
 *  - "twitter:ID" or "twitter_id:ID" → twitter_id=ID (global search)
 *  - "website:WEBSITE_ID" → website_id=WEBSITE_ID
 *  - "age:MIN-MAX" → age_min=MIN & age_max=MAX (either bound optional)
 *  - "size" or "size=true" → size=true
 *  - "count" or "count=true" → count=true (admin only)
 *  - otherwise the full string is sent as the free-text `query`
 */
const getUsers = (valueOrParams, pageNbr) => {
  const toParams = (obj) => {
    const params = new URLSearchParams();
    Object.entries(obj).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      // booleans should be sent as 'true'/'false'
      if (typeof v === 'boolean') params.set(k, v ? 'true' : 'false');
      else params.set(k, String(v));
    });
    return params.toString();
  };

  const defaults = {
    page_size: 10,
    page_nbr: pageNbr || 1,
    sort_field: 'created_at',
    sort_type: 'desc'
  };

  let builtParams = {};

  if (valueOrParams && typeof valueOrParams === 'object') {
    // params object path
    builtParams = { ...defaults, ...valueOrParams };

    // Support special syntaxes even when using params object with a `query` field
    const qObj = (valueOrParams.query || '').trim();
    if (qObj) {
      // social IDs from query string (global search)
      const mFbO = qObj.match(/^facebook(?:_id)?:\s*(.+)$/i);
      const mGgO = qObj.match(/^google(?:_id)?:\s*(.+)$/i);
      const mTwO = qObj.match(/^twitter(?:_id)?:\s*(.+)$/i);

      if (mFbO && !valueOrParams.facebook_id) {
        builtParams.facebook_id = mFbO[1].trim();
        delete builtParams.query;
      } else if (mGgO && !valueOrParams.google_id) {
        builtParams.google_id = mGgO[1].trim();
        delete builtParams.query;
      } else if (mTwO && !valueOrParams.twitter_id) {
        builtParams.twitter_id = mTwO[1].trim();
        delete builtParams.query;
      }
    }
  } else {
    // string query path (backward compatible)
    const q = (valueOrParams || '').trim();
    builtParams = { ...defaults };
    if (q) {
      const lower = q.toLowerCase();

      // social IDs
      const mFb = q.match(/^(facebook(?:_id)?:)\s*(.+)$/i);
      const mGg = q.match(/^(google(?:_id)?:)\s*(.+)$/i);
      const mTw = q.match(/^(twitter(?:_id)?:)\s*(.+)$/i);
      const mWebsite = q.match(/^website:\s*(.+)$/i);
      const mAge = q.match(/^age:\s*(\d+)?\s*-\s*(\d+)?$/i);
      const mProfileEmail = q.match(/^profile_email:\s*(.+)$/i);
      const mUid = q.match(/^uid:\s*(.+)$/i);

      if (mFb) {
        builtParams.facebook_id = mFb[2].trim();
      } else if (mGg) {
        builtParams.google_id = mGg[2].trim();
      } else if (mTw) {
        builtParams.twitter_id = mTw[2].trim();
      }

      if (mWebsite) {
        builtParams.website_id = mWebsite[1].trim();
      }

      if (mAge) {
        if (mAge[1]) builtParams.age_min = parseInt(mAge[1], 10);
        if (mAge[2]) builtParams.age_max = parseInt(mAge[2], 10);
      }

      if (mProfileEmail) {
        builtParams.profile_email = mProfileEmail[1].trim();
      }
      if (mUid) {
        builtParams.uid = mUid[1].trim();
      }

      if (lower === 'size' || lower === 'size=true') {
        builtParams.size = true;
      } else if (lower === 'count' || lower === 'count=true') {
        builtParams.count = true;
      }

      // If no special mapping applied, use free-text query (supports flags like is:ban, is:vip handled by backend)
      if (!builtParams.facebook_id && !builtParams.google_id && !builtParams.twitter_id && !builtParams.size && !builtParams.count && !builtParams.profile_email && !builtParams.uid) {
        builtParams.query = q;
      }
    }
  }

  const qs = toParams(builtParams);
  return http.get(`/users?${qs}`);
};

const websiteLimit = (userId, newValue) => {
  return http.put(`/suadmin/create_website_limit/${userId}`, { website_limit: newValue });
};

const deleteUser = (userId) => {
  return http.del(`/suadmin/user/${userId}`);
};

const deleteUserProfile = (websiteId, userId) => {
  return http.del(`/suadmin/website/${websiteId}/user/${userId}/profile`);
};

export default { getUsers, websiteLimit, deleteUser, deleteUserProfile };
export { getUsers, websiteLimit, deleteUser, deleteUserProfile };
